import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, App as AntdApp, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

import { organizationsAccessBlockCreateMutation } from 'src/api/main'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserQueries } from '../hooks'
import type { WorkspaceUser } from '../types'
import { composeDisplayName } from '../utils'

type Props = {
  workspaceId: number
  open: boolean
  user: WorkspaceUser | null
  /** `true` → suspend; `false` → reactivate. */
  suspend: boolean
  onClose: () => void
}

export const UserSuspendModal = ({
  workspaceId,
  open,
  user,
  suspend,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()

  const key = suspend ? 'suspend' : 'reactivate'

  const { mutateAsync, isPending } = useMutation({
    ...organizationsAccessBlockCreateMutation(),
    onSuccess: async () => {
      message.success(t(`users.modals.${key}.success` as const))
      if (user) {
        await invalidateWorkspaceUserQueries(qc, {
          workspaceId,
          organizationAccessId: user.id,
          userId: user.user.id
        })
      }
      onClose()
    },
    onError: (e) => showApiErrors(e)
  })

  const submit = async () => {
    if (!user) return
    await mutateAsync({
      path: { id: user.id },
      body: { blocked_value: suspend }
    })
  }

  const name = user
    ? composeDisplayName(
        user.user.first_name,
        user.user.last_name,
        user.user.email
      )
    : ''

  return (
    <Modal
      open={open}
      centered
      title={t(`users.modals.${key}.title` as const)}
      okText={t(`users.modals.${key}.submit` as const)}
      cancelText={t('users.modals.cancel')}
      okType={suspend ? 'danger' : 'primary'}
      okButtonProps={{ loading: isPending }}
      cancelButtonProps={{ disabled: isPending }}
      onOk={submit}
      onCancel={() => {
        if (!isPending) onClose()
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, color: 'var(--ant-color-text-secondary)' }}>
        {t(`users.modals.${key}.description` as const, { name })}
      </p>
      {suspend ? (
        <Alert
          type="warning"
          showIcon
          title={t('users.modals.suspend.warning')}
        />
      ) : null}
    </Modal>
  )
}
